// The guitar style's band (docs/AUDIO_DESIGN.md §12): a warm fingered electric bass and a soft, dusty lofi hip-hop kit.
// Persistent voices gated with setTargetAtTime, like instruments.js: nothing is allocated per hit.
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';

// Level knobs (the mix balance), against the jazzhop bass and kit they replace.
export const BAND = { bass: 0.78, kit: 1 };

/* ---------------- fingered electric bass: one periodic-wave oscillator, a plucked LP and a growl peak ---------------- */
/** Harmonics 1 · .5 · .26 · .13 · .07 · .035 · .018 → LP that opens to 2.8 × p.lp on the pluck and falls back to p.lp
 *  (380–480 Hz, τ 110 ms, Q .7) → peaking 720 Hz +2.5 dB (Q 1.1: the finger on the string, it carries on phones) → amp:
 *  τ 5 ms to 0.46v, to 0.6 × peak (τ 0.3 s), a long ring (τ 1.8 s from 0.5 s), release τ 50 ms. A slide comes up a
 *  semitone (τ 30 ms). Each note cancels what the last one left scheduled. p: { lp } */
export function createFingerBass(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const wave = ac.createPeriodicWave(new Float32Array(8), new Float32Array([0, 1, 0.5, 0.26, 0.13, 0.07, 0.035, 0.018]));
  const o = osc(ac, 'sine', 55), lp = filter(ac, 'lowpass', p.lp || 430, 0.7), growl = filter(ac, 'peaking', 720, 1.1, 2.5), amp = gain(ac, 0);
  o.setPeriodicWave(wave);
  o.connect(lp); lp.connect(growl); growl.connect(amp); amp.connect(out);
  const v = persistent(registry, tag, [o], [o, lp, growl, amp], t0), base = p.lp || 430;
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 0.46 * vel * BAND.bass, off = t + Math.max(0.08, dur);
      for (const prm of [o.frequency, lp.frequency, amp.gain]) hold(prm, t);
      if (slide) { o.frequency.setValueAtTime(f * Math.pow(2, -1 / 12), t); o.frequency.setTargetAtTime(f, t, 0.03); }
      else o.frequency.setValueAtTime(f, t);
      lp.frequency.setTargetAtTime(2.8 * base, t, 0.003); lp.frequency.setTargetAtTime(base, t + 0.012, 0.11);
      amp.gain.setTargetAtTime(A, t, 0.005);
      amp.gain.setTargetAtTime(0.6 * A, t + 0.03, 0.3);
      if (off > t + 0.5) amp.gain.setTargetAtTime(0, t + 0.5, 1.8);
      amp.gain.setTargetAtTime(0, off, 0.05);
    },
    dispose(t) { v.dispose(t); },
  };
}

/* ---------------- dusty kit: soft kick, soft snare, rim, hats (one noise source) ---------------- */
/** kit: { kickF0, kickF1, kickDecay, snareF, snareDecay, hatHP, hatClosed, rimF, lp }
 *  kick: sine kickF0 (112–132) → kickF1 (44–51) Hz (τ 38 ms), 0.52v, attack τ 2 ms, decay τ kickDecay (.15–.2 s); felt
 *        beater: noise → BP 900 Hz (Q .7), 0.035v, τ 6 ms (no click)
 *  snare: triangle 190 → 168 Hz (τ 30 ms) 0.16v, τ 45 ms + noise → HP 700 → BP snareF (1.5–2 kHz, Q .55) 0.34v, τ snareDecay
 *        (.09–.13 s; ghost 40 ms), reverb .22: a papery, dusty snare
 *  hat: noise → HP hatHP (5.2–6.8 kHz), 0.11v, τ hatClosed (14–22 ms; open 0.14 s), pan +.24, reverb .06
 *  rim: sine rimF (470–560) .6 + triangle 2.3·rimF .3 → BP 1.2 kHz (Q .9), 0.3v, τ 12 ms, pan −.16, reverb .25 (woody)
 *  all through the bus LP kit.lp (4.2–8.2 kHz) */
export function createDustKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime }) {
  const L = LEVELS.kit * BAND.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kBP = N(filter(ac, 'bandpass', 900, 0.7)), kClick = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); src.connect(kBP); kBP.connect(kClick); kClick.connect(bus);
  const sOsc = N(osc(ac, 'triangle', 180)), sTone = N(gain(ac, 0)), sSend = N(gain(ac, 0.22));
  const sHP = N(filter(ac, 'highpass', 700, 0.7)), sBP = N(filter(ac, 'bandpass', kit.snareF, 0.55)), sNoise = N(gain(ac, 0));
  sOsc.connect(sTone); sTone.connect(bus); sTone.connect(sSend);
  src.connect(sHP); sHP.connect(sBP); sBP.connect(sNoise); sNoise.connect(bus); sNoise.connect(sSend); sSend.connect(send);
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = N(gain(ac, 0)), hPan = N(panner(ac, 0.24)), hSend = N(gain(ac, 0.06));
  src.connect(hHP); hHP.connect(hAmp); hAmp.connect(hPan); hPan.connect(bus); hPan.connect(hSend); hSend.connect(send);
  const r1 = N(osc(ac, 'sine', kit.rimF)), r2 = N(osc(ac, 'triangle', 2.3 * kit.rimF)), rg1 = N(gain(ac, 0.6)), rg2 = N(gain(ac, 0.3));
  const rBP = N(filter(ac, 'bandpass', 1200, 0.9)), rAmp = N(gain(ac, 0)), rPan = N(panner(ac, -0.16)), rSend = N(gain(ac, 0.25));
  r1.connect(rg1); r2.connect(rg2); rg1.connect(rBP); rg2.connect(rBP); rBP.connect(rAmp); rAmp.connect(rPan);
  rPan.connect(bus); rPan.connect(rSend); rSend.connect(send);
  const p = persistent(registry, tag, [src, kOsc, sOsc, r1, r2], nodes, t0, 1.3);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.038);
      hit(kAmp.gain, 0.52 * v * L, t, 0.002, 0.008, kit.kickDecay);
      hit(kClick.gain, 0.035 * v * L, t, 0.0006, 0.002, 0.006);
    },
    snare(t, v, ghost = false) {
      sOsc.frequency.setValueAtTime(190, t); sOsc.frequency.setTargetAtTime(168, t, 0.03);
      hit(sTone.gain, 0.16 * v * L, t, 0.001, 0.004, 0.045);
      hit(sNoise.gain, 0.34 * v * L, t, 0.0015, 0.005, ghost ? 0.04 : kit.snareDecay);
    },
    hat(t, v, open = false) { hit(hAmp.gain, 0.11 * v * L, t, 0.0008, 0.003, open ? 0.14 : kit.hatClosed); },
    rim(t, v) { hit(rAmp.gain, 0.3 * v * L, t, 0.0006, 0.002, 0.012); },
    dispose(t) { p.dispose(t); },
  };
}
